import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import { apiService } from '../services/api';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';

interface SupportTicketModalProps {
  open: boolean;
  onClose: () => void;
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({ open, onClose }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useTelegramWebApp();

  const handleSubmit = async () => {
    if (!message.trim() || message.trim().length < 5) {
      setError('Пожалуйста, введите ваш вопрос (минимум 5 символов)');
      return;
    }

    if (!user) {
      setError('Пользователь не авторизован');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiService.submitSupportTicket({
        message: message.trim(),
        telegram_user_id: user.id
      });
      setShowSuccess(true);
      setMessage('');
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Не удалось отправить обращение. Попробуйте позже.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage('');
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: '#ffffff',
        }
      }}
    >
      {!showSuccess ? (
        <>
          <DialogTitle sx={{ pb: 1, pt: 2, px: 2 }}>
            <Typography variant="h6" component="div" sx={{ fontSize: '0.95rem' }}>
              Задайте свой вопрос:
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 1, px: 2 }}>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="Опишите вашу проблему или вопрос..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              error={!!error}
              helperText={error}
              sx={{ 
                mt: 0.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, pt: 1.5, gap: 1 }}>
            <Button 
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                color: '#283655',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(40, 54, 85, 0.08)',
                },
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || message.trim().length < 5}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#4d648d',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '24px',
                '&:hover': {
                  backgroundColor: '#283655',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(77, 100, 141, 0.4)',
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <Box sx={{ p: 2 }}>
          <Alert severity="success">
            <Typography variant="body1">
              Ваше обращение принято, ответ направим в телеграм-чат
            </Typography>
          </Alert>
        </Box>
      )}
    </Dialog>
  );
};

export default SupportTicketModal;

